import { useState, useMemo } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// ─── Icon helper ─────────────────────────────────────────
function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

// ─── Types ──────────────────────────────────────────
type EntryType = 'nueva_funcion' | 'mejora' | 'correccion' | 'seguridad';
type FilterType = 'todas' | EntryType;

interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  type: EntryType;
  description: string;
  changes?: string[];
}

// ─── Type badge config ──────────────────────────────
const TYPE_CONFIG: Record<
  EntryType,
  { label: string; color: string; bg: string; icon: string }
> = {
  nueva_funcion: {
    label: 'Nueva función',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50 border-emerald-200',
    icon: 'rocket_launch',
  },
  mejora: {
    label: 'Mejora',
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200',
    icon: 'bolt',
  },
  correccion: {
    label: 'Corrección',
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200',
    icon: 'bug_report',
  },
  seguridad: {
    label: 'Seguridad',
    color: 'text-red-700',
    bg: 'bg-red-50 border-red-200',
    icon: 'shield',
  },
};

// ─── Filter pills ───────────────────────────────────
const FILTERS: { key: FilterType; label: string }[] = [
  { key: 'todas', label: 'Todas' },
  { key: 'nueva_funcion', label: 'Nuevas funciones' },
  { key: 'mejora', label: 'Mejoras' },
  { key: 'correccion', label: 'Correcciones' },
];

// ─── Changelog data ─────────────────────────────────
const CHANGELOG_ENTRIES: ChangelogEntry[] = [
  {
    version: 'v2.5.0',
    date: '20 Feb 2026',
    title: 'Contabilización con IA mejorada',
    type: 'nueva_funcion',
    description:
      'Procesamiento inteligente de facturas con reconocimiento OCR avanzado y clasificación automática de asientos contables. El motor de IA ahora analiza patrones históricos para sugerir cuentas contables con mayor precisión.',
    changes: [
      'OCR avanzado con soporte para facturas escaneadas y fotografiadas',
      'Clasificación automática de asientos basada en historial',
      'Sugerencias inteligentes de cuentas contables',
      'Procesamiento en lote de hasta 50 facturas simultáneas',
    ],
  },
  {
    version: 'v2.4.2',
    date: '10 Feb 2026',
    title: 'Exportación Excel/PDF',
    type: 'nueva_funcion',
    description:
      'Exporta listados de documentos, proveedores y clientes en formato Excel o PDF. Incluye filtros personalizados y formatos configurables para adaptarse a las necesidades de cada asesoría.',
    changes: [
      'Exportación a Excel (.xlsx) con formato profesional',
      'Generación de PDF con cabecera personalizada',
      'Filtros de fecha, estado y tipo aplicables antes de exportar',
    ],
  },
  {
    version: 'v2.4.1',
    date: '28 Ene 2026',
    title: 'Panel de proveedores',
    type: 'nueva_funcion',
    description:
      'Gestión completa de proveedores con formulario de datos fiscales y parámetros contables. Permite asignar cuentas por defecto y configurar retenciones de forma centralizada.',
    changes: [
      'Formulario completo de datos fiscales y de contacto',
      'Asignación de cuentas contables por defecto',
      'Configuración de retenciones IRPF e IVA',
      'Búsqueda rápida por NIF, nombre o cuenta contable',
    ],
  },
  {
    version: 'v2.4.0',
    date: '15 Ene 2026',
    title: 'Factura electrónica mejorada',
    type: 'mejora',
    description:
      'Soporte ampliado para facturas simplificadas, presupuestos, albaranes y proformas. Cumplimiento actualizado con las normativas TicketBAI y Verifactu para facturación electrónica en España.',
    changes: [
      'Soporte para facturas simplificadas y rectificativas',
      'Generación de presupuestos y albaranes',
      'Compatibilidad con TicketBAI y Verifactu',
    ],
  },
  {
    version: 'v2.3.5',
    date: '5 Ene 2026',
    title: 'Papelera de documentos',
    type: 'nueva_funcion',
    description:
      'Recupera documentos eliminados con un solo clic. Los documentos permanecen en la papelera durante 30 días antes de ser eliminados definitivamente, dándote margen para corregir errores.',
    changes: [
      'Restauración de documentos con un clic',
      'Retención automática de 30 días',
      'Eliminación definitiva con confirmación',
    ],
  },
  {
    version: 'v2.3.0',
    date: '18 Dic 2025',
    title: 'Conciliación bancaria con IA',
    type: 'nueva_funcion',
    description:
      'Motor de conciliación automática con matching inteligente y parser Norma43. Analiza movimientos bancarios y los asocia automáticamente con facturas registradas, reduciendo el trabajo manual un 80%.',
    changes: [
      'Parser Norma43 para importación de extractos bancarios',
      'Matching inteligente por importe, fecha y concepto',
      'Panel de conciliación con vista dividida',
      'Reglas personalizables de matching automático',
    ],
  },
  {
    version: 'v2.2.0',
    date: '5 Dic 2025',
    title: 'Panel financiero avanzado',
    type: 'nueva_funcion',
    description:
      'Ratios de liquidez, solvencia, rentabilidad y apalancamiento con alertas configurables. Visualización gráfica de la evolución financiera con comparativas interanuales.',
    changes: [
      'Ratios financieros clave con semáforo de alertas',
      'Gráficos de evolución mensual y comparativa anual',
      'Exportación de informes financieros en PDF',
    ],
  },
  {
    version: 'v2.1.0',
    date: '15 Nov 2025',
    title: 'Chat de incidencias en tiempo real',
    type: 'nueva_funcion',
    description:
      'Comunicación bidireccional asesor-cliente con notificaciones en tiempo real mediante Supabase Realtime. Adjunta documentos y resuelve incidencias sin salir de la plataforma.',
    changes: [
      'Chat en tiempo real con notificaciones push',
      'Adjuntar documentos directamente en la conversación',
      'Historial completo de incidencias por empresa',
    ],
  },
  {
    version: 'v2.0.5',
    date: '3 Nov 2025',
    title: 'Corrección de rendimiento',
    type: 'correccion',
    description:
      'Optimización de carga de documentos y mejora de tiempos de respuesta en listados grandes. Reducción del tiempo de carga inicial en un 40% y mejora en la paginación de resultados.',
    changes: [
      'Carga diferida (lazy loading) de listados de documentos',
      'Paginación optimizada en servidor',
      'Cache de consultas frecuentes',
      'Reducción del bundle JS en 120KB',
    ],
  },
  {
    version: 'v2.0.0',
    date: '15 Oct 2025',
    title: 'Lanzamiento Externaliza2',
    type: 'nueva_funcion',
    description:
      'Plataforma completa de gestión contable para asesorías españolas. Digitaliza, contabiliza y gestiona toda la documentación de tus clientes desde un único panel centralizado.',
    changes: [
      'Dashboard centralizado para asesores y clientes',
      'Subida y clasificación automática de documentos',
      'Gestión de empresas y clientes con roles diferenciados',
      'Integración con Cegid y otros conectores contables',
      'Sistema de notificaciones por correo electrónico',
    ],
  },
];

// ─── Component ──────────────────────────────────────
export default function AsesorNovedades() {
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState<FilterType>('todas');
  const [allRead, setAllRead] = useState(false);

  const filteredEntries = useMemo(() => {
    if (activeFilter === 'todas') return CHANGELOG_ENTRIES;
    return CHANGELOG_ENTRIES.filter((entry) => entry.type === activeFilter);
  }, [activeFilter]);

  const handleMarkAllRead = () => {
    setAllRead(true);
    toast.success('Todas las novedades marcadas como leídas');
  };

  if (!user) return null;

  return (
    <Layout rol="asesor">
      <Head>
        <title>Novedades | Externaliza2</title>
      </Head>

      <div className="h-[calc(100vh-72px)] overflow-y-auto bg-calm-bg">
        <div className="px-12 pt-12 pb-12 space-y-10">
          {/* ═══════════════ HEADER ═══════════════ */}
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">
                Novedades
              </p>
              <h1 className="font-outfit text-5xl font-light text-calm-text tracking-tight">
                Actualizaciones
              </h1>
              <p className="text-sm text-calm-muted mt-2 font-light">
                Últimas mejoras y funcionalidades de la plataforma
              </p>
            </div>

            <button
              onClick={handleMarkAllRead}
              disabled={allRead}
              className="border border-calm-text rounded-huge px-5 py-2.5 text-sm font-light bg-calm-text text-white hover:bg-slate-800 transition-all disabled:opacity-40 flex items-center gap-2"
            >
              <Icon name="done_all" className="text-base" />
              {allRead ? 'Todas leídas' : 'Marcar como leídas'}
            </button>
          </div>

          {/* ═══════════════ FILTER PILLS ═══════════════ */}
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={cn(
                  'border rounded-huge px-4 py-2 text-sm font-light transition-all',
                  activeFilter === filter.key
                    ? 'border-calm-text bg-calm-text text-white'
                    : 'border-calm-border text-calm-muted hover:bg-white hover:text-calm-text'
                )}
              >
                {filter.label}
              </button>
            ))}

            <span className="ml-auto text-sm font-light text-calm-accent tabular-nums">
              {filteredEntries.length}{' '}
              {filteredEntries.length === 1
                ? 'actualización'
                : 'actualizaciones'}
            </span>
          </div>

          {/* ═══════════════ TIMELINE ═══════════════ */}
          <div className="relative">
            {/* Vertical timeline line */}
            <div className="absolute left-[15px] top-4 bottom-4 w-px bg-calm-border hidden md:block" />

            <div className="space-y-8">
              {filteredEntries.map((entry, index) => {
                const typeConfig = TYPE_CONFIG[entry.type];

                return (
                  <div
                    key={entry.version}
                    className="relative flex gap-8 group"
                    style={{
                      animation: `fadeSlideIn 0.4s ease-out ${index * 0.06}s both`,
                    }}
                  >
                    {/* Timeline dot */}
                    <div className="hidden md:flex flex-col items-center shrink-0">
                      <div className="relative z-10 w-[31px] h-[31px] rounded-full bg-white border border-calm-border flex items-center justify-center group-hover:border-calm-text transition-colors duration-200">
                        <div className="w-2 h-2 rounded-full bg-calm-text group-hover:scale-150 transition-transform duration-200" />
                      </div>
                    </div>

                    {/* Card */}
                    <div className="flex-1 bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 hover:shadow-sm transition-all duration-200">
                      {/* Card header */}
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        {/* Date */}
                        <span className="text-[11px] text-calm-accent font-light tabular-nums">
                          {entry.date}
                        </span>

                        <span className="w-px h-3 bg-calm-border" />

                        {/* Version badge */}
                        <Badge
                          variant="outline"
                          className="text-[10px] font-mono text-calm-text bg-calm-bg border-calm-border rounded-full gap-1"
                        >
                          <Icon name="sell" className="text-[11px]" />
                          {entry.version}
                        </Badge>

                        {/* Type badge */}
                        <Badge
                          variant="outline"
                          className={cn(
                            'rounded-full gap-1.5 text-[10px]',
                            typeConfig.bg,
                            typeConfig.color
                          )}
                        >
                          <Icon
                            name={typeConfig.icon}
                            className="text-[12px]"
                          />
                          {typeConfig.label}
                        </Badge>
                      </div>

                      {/* Title */}
                      <h3 className="text-lg font-light text-calm-text mb-2 group-hover:text-calm-text transition-colors duration-200">
                        {entry.title}
                      </h3>

                      {/* Description */}
                      <p className="text-sm text-calm-muted leading-relaxed mb-5 font-light">
                        {entry.description}
                      </p>

                      {/* Sub-changes list */}
                      {entry.changes && entry.changes.length > 0 && (
                        <ul className="space-y-2.5 border-t border-calm-border pt-5">
                          {entry.changes.map((change, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2.5 text-sm text-calm-muted font-light"
                            >
                              <Icon
                                name="chevron_right"
                                className="text-calm-accent text-[16px] mt-0.5 shrink-0"
                              />
                              <span>{change}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Empty state */}
          {filteredEntries.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="border-2 border-dashed border-fresh-sky-200/60 rounded-huge bg-gradient-to-br from-white to-fresh-sky-50/20 p-16 w-full max-w-lg">
                <div className="w-16 h-16 rounded-2xl bg-fresh-sky-50 flex items-center justify-center text-fresh-sky-400 mb-4 mx-auto">
                  <Icon
                    name="new_releases"
                    className="text-[40px]"
                  />
                </div>
                <p className="text-sm font-light text-calm-text mt-4">
                  No hay actualizaciones de este tipo por el momento.
                </p>
                <p className="text-[11px] text-calm-accent mt-1 font-light">
                  Prueba cambiando el filtro activo
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════ ANIMATION KEYFRAMES ═══════════════ */}
      <style jsx>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </Layout>
  );
}
